import { useEffect } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import {
  SettingsType,
  applyPipeexSettingsToConfigurationStore,
  buildPcfSettingsDtoFromStore,
  saveCurrentConfigurationToPipeex,
  setActivePipeexProfile,
  snapshotCurrentPcfSettingsStoreForDebug,
  summarizePcfSettingsDtoDataFlowForDebug,
  summarizePcfSettingsDtoForDebug,
  type PcfSettingsDto,
  type SettingInfoDto,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { buildProfileKey, resolveUniqueFlowSnapshotProfileName } from '../lib/profile';
import { warnMappingFlow } from '../lib/mappingTrace';
import { runPcfProfileCheckpoint } from '../lib/pcfProfileCheckpoint';
import {
  currentSetupRevisionId,
  debugMappingProfile,
  elapsedMs,
  errorMessage,
  nowMs,
  recordVerifiedSetupBaseline,
  startProfileStallWarning,
} from './profileControllerShared';
import type { SetupDocumentIdentity } from '../../../shared/lib/setupRevision';

interface FlowSnapshotProfileRef {
  name: string;
  settingsType: SettingsType;
  profileKey: string;
}

interface UseFlowSnapshotPersistenceArgs {
  enableFlowSnapshot: boolean;
  profileActivationReady: boolean;
  requireExplicitProfileSelection: boolean;
  restoreExistingProfileOnMount: boolean;
  /** `null` when no Revit document identifies the setup being persisted. */
  identity: SetupDocumentIdentity | null;
  onFlowSnapshotPersisted?: (profile: FlowSnapshotProfileRef) => void;
  loadProfiles: () => Promise<void>;
  loadProfilesInFlightRef: MutableRefObject<Promise<void> | null>;
  profilesRef: MutableRefObject<SettingInfoDto[]>;
  selectedProfileKeyRef: MutableRefObject<string>;
  flowSnapshotSavedRef: MutableRefObject<boolean>;
  flowSnapshotPersistingRef: MutableRefObject<boolean>;
  flowSnapshotSupersededRef: MutableRefObject<boolean>;
  /** Holds the claimed profile key while storage has not confirmed it. */
  flowSnapshotUnverifiedKeyRef: MutableRefObject<string | null>;
  setSelectedProfileKey: Dispatch<SetStateAction<string>>;
  setSelectedSettingsType: Dispatch<SetStateAction<SettingsType>>;
  setLastAppliedProfileKey: Dispatch<SetStateAction<string>>;
  setProfileError: Dispatch<SetStateAction<string | null>>;
}

const applyOptions = { resetProjectRuntimeState: false, restoreWizardProgress: false } as const;

/**
 * Canonicalizes the store through the profile shape before it is saved. This is
 * normalization, not trust: nothing here says the profile reached storage.
 */
const canonicalizeStoreForFlowSnapshot = (): void => {
  const localFlowProfile = buildPcfSettingsDtoFromStore(SettingsType.User);
  debugMappingProfile('[MappingProfileController] persistFlowSnapshot built local flow profile', {
    summary: summarizePcfSettingsDtoForDebug(localFlowProfile),
    dataFlow: summarizePcfSettingsDtoDataFlowForDebug(localFlowProfile),
    storeSnapshot: snapshotCurrentPcfSettingsStoreForDebug(),
  });
  applyPipeexSettingsToConfigurationStore(localFlowProfile, applyOptions);
};

/** Applies what storage really returned, once it has been verified as the setup just applied. */
const applyVerifiedFlowSnapshotProfile = (profile: PcfSettingsDto | null): void => {
  if (!profile) {
    return;
  }
  debugMappingProfile('[MappingProfileController] persistFlowSnapshot loaded normalized flow profile', {
    summary: summarizePcfSettingsDtoForDebug(profile),
    dataFlow: summarizePcfSettingsDtoDataFlowForDebug(profile),
  });
  applyPipeexSettingsToConfigurationStore(profile, applyOptions);
};

/**
 * Owns the one-shot "flow snapshot" profile persisted when the guided wizard
 * hands off to the mapping workspace. The refs are returned because sibling
 * controller slices (selection handling, save-as) coordinate through them.
 */
export const useFlowSnapshotPersistence = ({
  enableFlowSnapshot,
  profileActivationReady,
  requireExplicitProfileSelection,
  restoreExistingProfileOnMount,
  identity,
  onFlowSnapshotPersisted,
  loadProfiles,
  loadProfilesInFlightRef,
  profilesRef,
  selectedProfileKeyRef,
  flowSnapshotSavedRef,
  flowSnapshotPersistingRef,
  flowSnapshotSupersededRef,
  flowSnapshotUnverifiedKeyRef,
  setSelectedProfileKey,
  setSelectedSettingsType,
  setLastAppliedProfileKey,
  setProfileError,
}: UseFlowSnapshotPersistenceArgs) => {
  useEffect(() => {
    debugMappingProfile('[MappingProfileController] flow snapshot gate', {
      enableFlowSnapshot,
      profileActivationReady,
      requireExplicitProfileSelection,
      restoreExistingProfileOnMount,
      flowSnapshotSaved: flowSnapshotSavedRef.current,
    });
    if (
      !profileActivationReady ||
      !enableFlowSnapshot ||
      restoreExistingProfileOnMount ||
      flowSnapshotSavedRef.current
    ) {
      return;
    }

    flowSnapshotSavedRef.current = true;
    flowSnapshotPersistingRef.current = true;
    flowSnapshotSupersededRef.current = false;
    let isActive = true;

    /** Names the profile the workspace is working towards - a claim, never trust. */
    const claimFlowSnapshotSelection = (profileKey: string) => {
      selectedProfileKeyRef.current = profileKey;
      setSelectedProfileKey(profileKey);
      setSelectedSettingsType(SettingsType.User);
    };

    /** Selects the claimed profile and marks it as not yet confirmed by storage. */
    const claimUnverifiedFlowSnapshot = (profileKey: string) => {
      flowSnapshotUnverifiedKeyRef.current = profileKey;
      claimFlowSnapshotSelection(profileKey);
    };

    const trustFlowSnapshotProfile = (name: string, profileKey: string) => {
      flowSnapshotUnverifiedKeyRef.current = null;
      setLastAppliedProfileKey(profileKey);
      setActivePipeexProfile(name, SettingsType.User);
      claimFlowSnapshotSelection(profileKey);
      onFlowSnapshotPersisted?.({ name, settingsType: SettingsType.User, profileKey });
    };

    const persistFlowSnapshot = async () => {
      const startedAt = nowMs();
      let stallTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
      let flowSnapshotName = 'flow snapshot';
      try {
        const inFlightLoad = loadProfilesInFlightRef.current;
        if (inFlightLoad) {
          await inFlightLoad;
        }
        if (!isActive || flowSnapshotSupersededRef.current) {
          return;
        }
        flowSnapshotName = resolveUniqueFlowSnapshotProfileName(profilesRef.current);
        const flowSnapshotProfileKey = buildProfileKey(flowSnapshotName, SettingsType.User);
        stallTimer = startProfileStallWarning(
          'flow snapshot persist still pending',
          {
            profileKey: flowSnapshotProfileKey,
          },
          startedAt
        );
        canonicalizeStoreForFlowSnapshot();
        if (!isActive) {
          return;
        }
        claimUnverifiedFlowSnapshot(flowSnapshotProfileKey);

        const revisionId = currentSetupRevisionId();
        const checkpoint = await runPcfProfileCheckpoint({
          revisionId,
          currentRevisionId: currentSetupRevisionId,
          identity,
          target: { name: flowSnapshotName, settingsType: SettingsType.User },
          save: () => saveCurrentConfigurationToPipeex(flowSnapshotName, SettingsType.User),
          isSuperseded: () => !isActive || flowSnapshotSupersededRef.current,
        });
        if (!isActive || checkpoint.kind === 'superseded') {
          return;
        }
        if (checkpoint.kind === 'mismatch') {
          warnMappingFlow('flow snapshot read-back rejected', {
            elapsedMs: elapsedMs(startedAt),
            profileKey: flowSnapshotProfileKey,
            revisionId,
            reason: checkpoint.reason,
          });
          setProfileError(checkpoint.message);
          return;
        }

        applyVerifiedFlowSnapshotProfile(checkpoint.profile);
        if (!isActive) {
          return;
        }
        // The snapshot was saved and read back for this revision, so it is the
        // setup a Discard should return to until a later save proves another.
        recordVerifiedSetupBaseline({
          profileKey: flowSnapshotProfileKey,
          setupFingerprint: checkpoint.setupFingerprint,
          revisionId,
          identity,
        });
        trustFlowSnapshotProfile(flowSnapshotName, flowSnapshotProfileKey);
        await loadProfiles();
        debugMappingProfile('[MappingProfileController] persistFlowSnapshot complete', {
          elapsedMs: elapsedMs(startedAt),
          profileKey: flowSnapshotProfileKey,
        });
      } catch (error) {
        if (!isActive) {
          return;
        }
        warnMappingFlow('flow snapshot persist failed', {
          elapsedMs: elapsedMs(startedAt),
          error: errorMessage(error),
        });
        setProfileError(
          error instanceof Error
            ? error.message
            : `Failed to persist flow snapshot to ${flowSnapshotName} profile`
        );
      } finally {
        if (stallTimer) {
          globalThis.clearTimeout(stallTimer);
        }
        flowSnapshotPersistingRef.current = false;
      }
    };

    void persistFlowSnapshot();

    return () => {
      isActive = false;
    };
  }, [
    enableFlowSnapshot,
    flowSnapshotPersistingRef,
    flowSnapshotSavedRef,
    flowSnapshotSupersededRef,
    flowSnapshotUnverifiedKeyRef,
    identity,
    loadProfiles,
    loadProfilesInFlightRef,
    onFlowSnapshotPersisted,
    profileActivationReady,
    profilesRef,
    requireExplicitProfileSelection,
    restoreExistingProfileOnMount,
    selectedProfileKeyRef,
    setLastAppliedProfileKey,
    setProfileError,
    setSelectedProfileKey,
    setSelectedSettingsType,
  ]);
};
